import React, { useState, useRef, useEffect, useReducer, Reducer } from 'react'
import QrScanner from 'qr-scanner'
import './room_audit/styles.css'
import { beep } from './room_audit/beep'
import $ from 'jquery';

const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

function Instructions({ message, latest, cage, count }) {
  return (
    <>
      <h3>{message}</h3>
      <h4>{latest}</h4>
      {cage && (
        <div>
          {cage.retired && <h4>Cage retired</h4>}
          <h5>Cage size: {cage.size}</h5>
          <h5>Number of animals: {cage.number_of_animals}</h5>
        </div>
      )}
      <h5>{count} Scanned</h5>
    </>
  )
}

function QRScanner({ onScan, isModalOpen }) {
  const ref = useRef()
  const [scanner, setScanner] = useState<QrScanner>()
  const [scanned, setScanned] = useState(null)

  useEffect(() => {
    if (scanned) {
      onScan(scanned)
    }
  }, [scanned])

  useEffect(() => {
    if (!isModalOpen || !ref.current) return;

    const s = new QrScanner(ref.current, (result) => setScanned(result), {
      onDecodeError: (e) => setScanned(e),
      highlightScanRegion: true,
      highlightCodeOutline: true,
      returnDetailedScanResult: true,
      preferredCamera: 'environment',
      calculateScanRegion: (video) => {
        const scanRegion = {
          x: Math.max(0, (video.videoWidth - 300) / 2),
          y: Math.max(0, (video.videoHeight - 300) / 2),
          width: Math.min(300, video.videoWidth),
          height: Math.min(300, video.videoHeight)
        }
        return scanRegion
      }
    })
    setScanner(s)
    s.start()

    return () => {
      s.stop()
      s.destroy()
      setScanner(null)
    }
  }, [isModalOpen])

  return <video className='qr-scanner-video' ref={ref}></video>
}

interface ScanTrackerState {
  scannedCageCards: Set<number>
  scannedCages: Set<number>
  last: number | null
}

function useCageCardScanTracker() {
  const [state, setState] = useReducer<Reducer<ScanTrackerState, Partial<ScanTrackerState>>>(
    (currentState, newState) => ({ ...currentState, ...newState }),
    { scannedCageCards: new Set<number>(), scannedCages: new Set<number>(), last: null }
  )

  function scannedLast(cageCardId: number) {
    return cageCardId === state.last
  }

  function scannedBefore(cageCardId: number) {
    return state.scannedCageCards.has(cageCardId)
  }

  function noteScanned(cageCardId: number | null, cageId: number | null) {
    if (cageCardId && cageId) {
      const newScannedCageCards = new Set(state.scannedCageCards).add(cageCardId)
      const newScannedCages = new Set(state.scannedCages).add(cageId)
      setState({ scannedCageCards: newScannedCageCards, scannedCages: newScannedCages, last: cageCardId })
    } else {
      setState({ scannedCageCards: state.scannedCageCards, scannedCages: state.scannedCages, last: null })
    }
  }

  function reset() {
    setState({ scannedCageCards: new Set<number>(), scannedCages: new Set<number>(), last: null })
  }

  return {
    scannedLast,
    scannedBefore,
    noteScanned,
    reset,
    scannedCageCards: state.scannedCageCards,
    scannedCages: state.scannedCages
  }
}

function cageCardIdFromQRCode(result: any): number | undefined {
  const urlString = result.data.startsWith('//')
    ? `https:${result.data}`
    : result.data.startsWith('https://')
    ? result.data
    : `https://${result.data}`

  const url = new URL(urlString)
  const parts = url.pathname.split('/')

  if (parts[0] === '' && parts[1] === 'cage_cards' && parts[3] === 'scan') {
    return parseInt(parts[2])
  }
  if (parts[0] === '' && parts[1] === 'c') {
    return parseInt(parts[2])
  }
}

function ScannedList({ scanned }) {
  const scannedCageCardNumbers = Array.from(scanned.values())
    .map((n) => '#' + n)
    .join(', ')
  return <div>Scanned cages: {scannedCageCardNumbers}</div>
}

function changeHighlightColor(color: string) {
  const highlightSvg = document.querySelector('.scan-region-highlight-svg') as SVGElement
  const codeOutlineSvg = document.querySelector('.code-outline-highlight') as SVGElement

  if (highlightSvg && codeOutlineSvg) {
    highlightSvg.style.stroke = color
    codeOutlineSvg.style.stroke = color
  }
}

async function fetchCage(cageCardId: number) {
  try {
    const response = await fetch(`/cage_cards/${cageCardId}/cage_info`)
    if (!response.ok) {
      throw new Error('Failed to fetch Cage information')
    }
    const data = await response.json()
    return data
  } catch (error) {
    return null
  }
}

async function submitScannedCages(submitURL: string, cageIds: number[]) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  const response = await fetch(submitURL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken || ''
    },
    body: JSON.stringify({ cage_card_ids: cageIds })
  })
  return response.ok
}

export default function TransferScan(props: { submit_url: string }) {
  const { scannedLast, scannedBefore, noteScanned, reset, scannedCageCards, scannedCages } = useCageCardScanTracker()
  const [instructions, setInstructions] = useState('Point the camera at a cage card QR code for Transfer Line Item')
  const [latest, setLatest] = useState('')
  const [audioEnabled, setAudioEnabled] = useState(!isMobileDevice)
  const [cage, setCage] = useState<any>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const modalElement = document.getElementById('transfer-scanner-modal')

    const checkModalStatus = () => {
      if (modalElement) {
        const isOpen = modalElement.classList.contains('show') ||
                      modalElement.style.display === 'block' ||
                      window.getComputedStyle(modalElement).display === 'block'
        setIsModalOpen(isOpen)
      }
    }

    checkModalStatus()

    if (modalElement) {
      const observer = new MutationObserver(() => {
        checkModalStatus()
      })

      observer.observe(modalElement, {
        attributes: true,
        attributeFilter: ['class', 'style']
      })

      const handleModalShow = () => setIsModalOpen(true)
      const handleModalHide = () => setIsModalOpen(false)

      modalElement.addEventListener('show.bs.modal', handleModalShow)
      modalElement.addEventListener('hide.bs.modal', handleModalHide)

      if (typeof $ !== 'undefined') {
        $(modalElement).on('show.bs.modal', handleModalShow)
        $(modalElement).on('hide.bs.modal', handleModalHide)
      }

      const pollInterval = setInterval(checkModalStatus, 500)

      return () => {
        observer.disconnect()
        clearInterval(pollInterval)
        modalElement.removeEventListener('show.bs.modal', handleModalShow)
        modalElement.removeEventListener('hide.bs.modal', handleModalHide)
        if (typeof $ !== 'undefined') {
          $(modalElement).off('show.bs.modal', handleModalShow)
          $(modalElement).off('hide.bs.modal', handleModalHide)
        }
      }
    }
  }, [])

  async function onScan(result: any) {
    if (isScanning) {
      return
    }

    if (typeof result === 'string' || result.data === '') {
      noteScanned(null, null)
      changeHighlightColor('#000000')
      return
    }

    const cageCardId = cageCardIdFromQRCode(result)
    if (cageCardId) {
      setIsScanning(true)

      try {
        const cageData = await fetchCage(cageCardId)
        if (!cageData || !cageData.id) {
          setLatest(`No Cage found for Cage Card #${cageCardId}`)
          return
        }

        const cageId = cageData.id

        if (scannedLast(cageCardId)) {
          await new Promise(delay => setTimeout(delay, 1000));
          noteScanned(cageCardId, cageId);
          beep('existing')
          setLatest(`Already Scanned Cage #${cageId}`);
          setCage(cageData);
          changeHighlightColor('#f77600');
        } else if (scannedBefore(cageCardId)) {
          noteScanned(cageCardId, cageId)
          beep('existing')
          setLatest(`Already Scanned Cage #${cageId}`)
          setCage(cageData)
          changeHighlightColor('#f77600')
        } else {
          noteScanned(cageCardId, cageId)
          beep('new')
          setLatest(`Scanned Cage #${cageId}`)
          setCage(cageData)
          changeHighlightColor('#00ff00')
        }
      } finally {
        setIsScanning(false)
      }
    } else if (result.data) {
      setLatest('Not a recognized cage card QR code')
    }
  }

  useEffect(() => {
    if (isMobileDevice) {
      window.addEventListener('click', () => setAudioEnabled(true), { once: true })
      window.addEventListener('touchstart', () => setAudioEnabled(true), { once: true })

      return () => {
        window.removeEventListener('click', () => setAudioEnabled(true))
        window.removeEventListener('touchstart', () => setAudioEnabled(true))
      }
    }
  }, [])

  async function handleSubmit() {
    setSubmitting(true)
    try {
      const success = await submitScannedCages(props.submit_url, Array.from(scannedCages))
      if (success) {
        const event = new CustomEvent('cages:submitted', { detail: Array.from(scannedCages) })
        document.dispatchEvent(event)

        reset()
        setLatest('')
        setCage(null)
        setInstructions('Point the camera at a cage card QR code for Transfer Line Item')

      } else {
        console.error('Failed to submit scanned cages.')
      }
    } catch (error) {
      console.error('Error submitting scanned cages:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Instructions message={instructions} latest={latest} cage={cage} count={scannedCageCards.size} />
      <div id='transfer-scan-container'>
        <QRScanner onScan={onScan} isModalOpen={isModalOpen} />
      </div>
      <ScannedList scanned={scannedCages} />
      <button onClick={handleSubmit} disabled={submitting} style={{ fontSize: '16px' }} className="detail-header-link">
        {submitting ? 'Submitting...' : 'Submit'}
      </button>
    </>
  )
}
